<p-dialog
  [(visible)]="renameDialogVisible"
  [modal]="true"
  [closable]="!editModeSaving"
  [closeOnEscape]="!editModeSaving"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: 'min(92vw, 30rem)' }"
  styleClass="dashboard-rename-dialog"
  (onHide)="cancelRenameWidget()">
  <ng-template pTemplate="header">
    <span class="dashboard-rename-title">Update widget</span>
  </ng-template>

  <div class="dashboard-rename-dialog-content">
    <div class="dashboard-rename-card">
      <input
        id="dashboardRenameTitle"
        class="dashboard-rename-input"
        type="text"
        [(ngModel)]="renameTitle"
        [disabled]="editModeSaving"
        placeholder="Widget title"
        maxlength="150" />
      <input
        id="dashboardRenameSubtitle"
        class="dashboard-rename-input"
        type="text"
        [(ngModel)]="renameSubtitle"
        [disabled]="editModeSaving"
        placeholder="Subtitle"
        maxlength="250" />
      <textarea
        id="dashboardRenameFooter"
        class="dashboard-rename-input dashboard-rename-textarea"
        [(ngModel)]="renameFooterText"
        [disabled]="editModeSaving"
        placeholder="Footer text"
        maxlength="250"
        rows="3"></textarea>

      <div class="dashboard-icon-picker" role="radiogroup" aria-label="Widget icon">
        @for (option of widgetIconOptions; track option.icon) {
          <button
            type="button"
            class="dashboard-icon-option"
            [class.dashboard-icon-option-selected]="renameIcon === option.icon"
            [attr.aria-checked]="renameIcon === option.icon"
            [attr.aria-label]="option.label"
            [title]="option.label"
            role="radio"
            [disabled]="editModeSaving"
            (click)="renameIcon = option.icon">
            <span class="material-symbols-outlined">{{option.icon}}</span>
          </button>
        }
      </div>
    </div>

    @if (renameErrorMessage) {
      <p class="dashboard-rename-error">{{renameErrorMessage | customerText}}</p>
    }
  </div>

  <ng-template pTemplate="footer">
    <button type="button" class="btn-inverse" (click)="cancelRenameWidget()" [disabled]="editModeSaving">
      <span>Cancel</span>
    </button>
    <button type="button" class="btn-primary" (click)="confirmRenameWidget()" [disabled]="editModeSaving">
      <span>{{editModeSaving ? 'Saving...' : 'Save'}}</span>
    </button>
  </ng-template>
</p-dialog>

<div class="dashboard-page p-3 md:p-5 bg-grey-50 min-h-full lg:border-l lg:border-grey-300">
  <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between mb-4">
    <div>
      <h1 class="p-0">{{isBluebirdDemo ? 'Bluebird Care Quality Dashboard' : 'Dashboards'}}</h1>
      <p class="text-grey-900 mt-1">
        {{isBluebirdDemo ? 'A clear view of quality, compliance and action performance across the network.' : 'Care audit performance, action follow-up, and compliance insight.'}}
      </p>
    </div>
    <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
      <span class="text-grey-900 text-sm">Last updated: {{lastUpdatedDisplay || (selectionPrompt ? 'Not loaded' : 'Loading')}}</span>
      @if (canEnterEditMode) {
      <button class="btn-inverse" type="button" (click)="toggleEditMode()">
        @if (!editMode) {
          <span>Edit layout</span>
        }
        @if (editMode) {
          <span>Exit edit mode</span>
        }
      </button>
      }
    </div>
  </div>

  <div class="bg-standardwhite border border-grey-300 rounded-md shadow-sm mb-4">
    <button
      type="button"
      class="w-full p-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between text-left"
      (click)="toggleFilters()"
      [attr.aria-expanded]="filtersExpanded">
      <div class="flex flex-wrap items-center gap-2">
        @for (item of filterSummaryItems; track item.label) {
          <span class="filter-chip">
            <span class="filter-chip-label">{{item.label}}</span>
            <span class="filter-chip-value">{{item.value}}</span>
          </span>
        }
      </div>
      <span class="flex items-center gap-1 text-green-700 font-semibold whitespace-nowrap">
        <span>{{filtersExpanded ? 'Hide filters' : 'Show filters'}}</span>
        <span class="material-symbols-outlined">{{filtersExpanded ? 'expand_less' : 'expand_more'}}</span>
      </span>
    </button>

    @if (filtersExpanded) {
      <div class="dashboard-filter-grid border-t border-grey-300 p-4">
        @if (filterNotice) {
          <p class="dashboard-filter-notice text-sm text-grey-700">{{filterNotice}}</p>
        }
        <div class="flex flex-col gap-1">
          <label for="dashboardSelector">Dashboard</label>
          <select id="dashboardSelector" class="text-box" [(ngModel)]="selectedDashboardId" (change)="onDashboardChange()">
            @for (dashboard of dashboards; track dashboard.DashboardID) {
              <option [ngValue]="dashboard.DashboardID">{{dashboardDisplayName(dashboard)}}</option>
            }
          </select>
        </div>

        @if (!usesSimplifiedDashboardFilters) {
          <div class="flex flex-col gap-1">
            <label for="startDate">Start date</label>
            <input id="startDate" class="text-box" type="date" [(ngModel)]="filters.StartDate" />
          </div>

          <div class="flex flex-col gap-1">
            <label for="endDate">End date</label>
            <input id="endDate" class="text-box" type="date" [(ngModel)]="filters.EndDate" />
          </div>
        }

        @if (showScopeSelector) {
        <div class="flex flex-col gap-1">
          <label for="dashboardScope">Network / Region</label>
          <select id="dashboardScope" class="text-box" [ngModel]="selectedScopeKey" (ngModelChange)="onScopeFilterChange($event)">
            @for (scope of scopeFilterOptions; track scope.ScopeType + ':' + (scope.ScopeID ?? '')) {
              <option [ngValue]="scope.ScopeType + ':' + (scope.ScopeID ?? '')">{{scope.Label}} ({{scope.OfficeCount}} offices)</option>
            }
          </select>
        </div>
        }

        @if (showOfficeSelector) {
        <div class="flex flex-col gap-1">
          <label for="locationName">Office</label>
          <select id="locationName" class="text-box" [(ngModel)]="filters.LocationID" (ngModelChange)="onLocationFilterChange($event)">
            <option [ngValue]="null" disabled>Please select</option>
            @for (location of locationFilterOptions; track location.ID) {
              <option [ngValue]="location.ID">{{location.Label}}</option>
            }
          </select>
        </div>
        }

        @if (showQualityManagerSelector) {
        <div class="flex flex-col gap-1">
          <label for="qualityManager">Regional quality assurance lead</label>
          <select id="qualityManager" class="text-box" [(ngModel)]="filters.QualityManagerID" (ngModelChange)="onQualityManagerFilterChange($event)">
            <option [ngValue]="null">All regional quality assurance leads</option>
            @for (manager of visibleQualityManagerFilterOptions; track manager.ID) {
              <option [ngValue]="manager.ID">{{portfolioOptionLabel(manager)}}</option>
            }
          </select>
        </div>
        }

        @if (showBusinessDevelopmentManagerSelector) {
        <div class="flex flex-col gap-1">
          <label for="businessDevelopmentManager">Business development manager</label>
          <select id="businessDevelopmentManager" class="text-box" [(ngModel)]="filters.BusinessDevelopmentManagerID" (ngModelChange)="onBusinessDevelopmentManagerFilterChange($event)">
            <option [ngValue]="null">All business development managers</option>
            @for (manager of visibleBusinessDevelopmentManagerFilterOptions; track manager.ID) {
              <option [ngValue]="manager.ID">{{portfolioOptionLabel(manager)}}</option>
            }
          </select>
        </div>
        }
        @if (usingTemporaryPortfolioFilters) {
          <p class="text-sm text-warning-700 md:col-span-2">
            Temporary portfolio preview: selector interaction is enabled, but dashboard data is not filtered.
          </p>
        }

        @if (showAuditFilters) {
        <div class="flex flex-col gap-1">
          <label for="auditType">{{ 'audit' | customerTerm }} type</label>
          <select id="auditType" class="text-box" [(ngModel)]="filters.AuditTypeID" (ngModelChange)="onAuditTypeFilterChange($event)">
            @for (auditType of auditTypeFilterOptions; track auditType.ID) {
              <option [ngValue]="auditType.ID">{{auditType.Label}}</option>
            }
          </select>
        </div>

        <div class="flex flex-col gap-1">
          <label for="frequency">Frequency</label>
          <select id="frequency" class="text-box" [(ngModel)]="filters.FrequencyID" (ngModelChange)="onFrequencyFilterChange($event)">
            @for (frequency of frequencyFilterOptions; track frequency.ID) {
              <option [ngValue]="frequency.ID">{{frequency.Label}}</option>
            }
          </select>
        </div>

        <div class="flex flex-col gap-1">
          <label for="auditName">{{ 'audit' | customerTerm }}</label>
          <select id="auditName" class="text-box" [(ngModel)]="filters.AuditID" (ngModelChange)="onAuditFilterChange($event)">
            @for (audit of auditFilterOptions; track audit.ID) {
              <option [ngValue]="audit.ID">{{audit.Label}}</option>
            }
          </select>
        </div>
        }

        <div class="dashboard-filter-actions flex flex-col gap-3 justify-end">
          @if (showAuditFilters) {
          <label class="flex items-center gap-2 bg-grey-100 rounded-md px-3 py-2 border border-grey-300">
            <input class="rounded border-grey-300 text-green-700 focus:ring-green-300" type="checkbox" [(ngModel)]="filters.IncludeSublocations" />
            <span>Include sublocations</span>
          </label>
          }
          <button class="btn-primary" type="button" (click)="applyFilters()">Apply filters</button>
        </div>
      </div>
    }
  </div>

  @if (editMode) {
    <div class="bg-offwhite-500 border border-grey-300 rounded-md shadow-sm p-3 mb-4 flex flex-col gap-3">
      <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <p class="text-grey-900">{{editModeMessage || 'Copy a standard dashboard before customising widgets.'}}</p>
        <div class="flex flex-wrap items-center gap-2">
          @if (selectedDashboardCapabilities.CanCopyDashboard) {
            <button type="button" class="btn-inverse" (click)="copyCurrentDashboard()" [disabled]="editModeSaving">Copy dashboard</button>
          }
          @if (selectedDashboardCapabilities.CanManageWidgets) {
            <button type="button" class="btn-primary" (click)="addWidgetPreview()" [disabled]="editModeSaving">Add widget</button>
          }
        </div>
      </div>

      @if (addWidgetPanelVisible) {
        <div class="dashboard-add-widget-panel border-t border-grey-300 pt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
          <div class="flex flex-col gap-1">
            <label for="dashboardMetricPicker">Metric</label>
            <select id="dashboardMetricPicker" class="text-box" [(ngModel)]="selectedMetricDefinitionID">
              @for (metric of metricCatalogue; track metric.MetricDefinitionKey) {
                <option [ngValue]="metric.MetricDefinitionKey">{{metric.Name}}</option>
              }
            </select>
          </div>
          <button type="button" class="btn-primary" (click)="addSelectedWidget()" [disabled]="editModeSaving || metricCatalogue.length === 0">Add selected metric</button>
        </div>
      }
    </div>
  }

  @if (isLoading) {
    <div
      class="bg-standardwhite border border-grey-300 rounded-md shadow-sm p-4 mb-4 flex items-center gap-3"
      role="status"
      aria-live="polite"
      aria-busy="true">
      <span class="material-symbols-outlined text-green-300 text-4xl animate-spin" aria-hidden="true">progress_activity</span>
      <div>
        <h3>Loading dashboard</h3>
        <p>Please wait while we load your dashboard information.</p>
      </div>
    </div>

    <div class="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4" aria-hidden="true">
      @for (placeholder of [1, 2, 3, 4, 5, 6]; track placeholder) {
        <div class="bg-standardwhite border border-grey-300 rounded-md shadow-sm p-4 flex flex-col min-h-[13rem] gap-4">
          <div class="rounded-md bg-grey-300 animate-pulse h-5 w-2/3"></div>
          <div class="rounded-md bg-grey-300 animate-pulse h-12 w-1/2"></div>
          <div class="rounded-md bg-grey-300 animate-pulse h-4 w-5/6 mt-auto"></div>
        </div>
      }
    </div>
  }

  @if (!isLoading && selectionPrompt.length > 0) {
    <div class="bg-standardwhite border border-grey-300 rounded-md shadow-sm p-5 flex items-start gap-3">
      <span class="material-symbols-outlined text-green-300 text-4xl">location_on</span>
      <div>
        <h3>{{selectionPromptHeading}}</h3>
        <p>{{selectionPrompt}}</p>
      </div>
    </div>
  }

  @if (!isLoading && selectionPrompt.length === 0 && errorMessage.length > 0) {
    <div class="bg-danger-surface border border-danger-base rounded-md shadow-sm p-5 flex items-start gap-3">
      <span class="material-symbols-outlined text-danger-base text-4xl">error</span>
      <div>
        <h3>Dashboard unavailable</h3>
        <p>{{errorMessage}}</p>
      </div>
    </div>
  }

  @if (!isLoading && selectionPrompt.length === 0 && errorMessage.length === 0 && widgets.length === 0) {
    <div class="bg-standardwhite border border-grey-300 rounded-md shadow-sm p-5 flex items-start gap-3">
      <span class="material-symbols-outlined text-green-300 text-4xl">inbox</span>
      <div>
        <h3>No dashboard data</h3>
        <p>No widgets matched the current dashboard and filter selection.</p>
      </div>
    </div>
  }

  @if (!isLoading && selectionPrompt.length === 0 && errorMessage.length === 0 && widgets.length > 0) {
    <div class="dashboard-content-grid" [class.dashboard-content-grid-drilldown]="selectedWidget || drilldownLoading">
      @if (usesBluebirdGroupedLayout && !selectedWidget && !drilldownLoading) {
      <div class="dashboard-bluebird-sections">
        @for (section of bluebirdWidgetSections; track section.key) {
          <section class="dashboard-bluebird-section" [attr.aria-labelledby]="'dashboard-section-' + section.key">
            <h2 class="dashboard-section-title" [id]="'dashboard-section-' + section.key">{{section.title}}</h2>
            <div class="dashboard-bluebird-section-grid">
              @for (widget of section.widgets; track widget.DashboardWidgetID) {
                <app-dashboard-widget
                  [attr.data-dashboard-metric]="widget.MetricDefinitionKey"
                  [widget]="widget"
                  [selected]="false"
                  [editMode]="editMode && selectedDashboardCapabilities.CanManageWidgets"
                  [presentationMode]="isBluebirdDemo"
                  [compactMode]="false"
                  [summaryPagingLoading]="summaryPagingWidgetIDs.has(widget.DashboardWidgetID)"
                  (widgetSelected)="selectWidget($event)"
                  (summaryPageChanged)="changeSummaryPage($event)"
                  (removeWidget)="removeWidgetPreview($event)"
                  (renameWidget)="renameWidgetPreview($event)"
                  (configureWidget)="configureWidgetPreview($event)">
                </app-dashboard-widget>
              }
            </div>
          </section>
        }
      </div>
      } @else {
      <div
        class="dashboard-widget-grid"
        [class.dashboard-widget-grid-demo]="isBluebirdDemo"
        [class.dashboard-widget-grid-drilldown]="selectedWidget || drilldownLoading">
        @for (widget of widgets; track widget.DashboardWidgetID) {
          <app-dashboard-widget
            [widget]="widget"
            [selected]="selectedWidget?.DashboardWidgetID === widget.DashboardWidgetID"
            [editMode]="editMode && selectedDashboardCapabilities.CanManageWidgets"
            [presentationMode]="isBluebirdDemo"
            [compactMode]="!!selectedWidget || drilldownLoading"
            [summaryPagingLoading]="summaryPagingWidgetIDs.has(widget.DashboardWidgetID)"
            (widgetSelected)="selectWidget($event)"
            (summaryPageChanged)="changeSummaryPage($event)"
            (removeWidget)="removeWidgetPreview($event)"
            (renameWidget)="renameWidgetPreview($event)"
            (configureWidget)="configureWidgetPreview($event)">
          </app-dashboard-widget>
        }
      </div>
      }

      @if (selectedWidget || drilldownLoading) {
        <div class="dashboard-drilldown-region" #drilldownRegion tabindex="-1" aria-label="Widget drill-down" (keydown.escape)="closeDrilldown()">
          <app-dashboard-drilldown
            [drilldown]="drilldown"
            [loading]="drilldownLoading"
            [sortField]="drilldownSortField"
            [sortDirection]="drilldownSortDirection"
            (closePanel)="closeDrilldown()"
            (pageChanged)="changeDrilldownPage($event)"
            (sortChanged)="changeDrilldownSort($event)"
            (selectionOpened)="openDrilldownSelection($event)">
          </app-dashboard-drilldown>
        </div>
      }
    </div>
  }
</div>
